/**
 * Pin type definitions for the message pin system
 * Configurable system for determining which chat messages should appear as map pins
 */

// Helper function to validate location coordinates
const isValidLocation = (location) => {
  if (!location || typeof location !== 'object') return false;
  const { lat, lng } = location;
  return (
    typeof lat === 'number' && 
    typeof lng === 'number' && 
    !isNaN(lat) && 
    !isNaN(lng) &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180
  );
};

// Helper function to get standardized metadata
const getMetadata = (message) => {
  // Prioritize message_metadata (database field) over metadata (frontend field)
  return message.message_metadata || message.metadata || {};
};

// Helper function to get standardized timestamp
const getTimestamp = (message) => {
  // Prioritize createdAt (database field) over timestamp (frontend field)
  return message.createdAt || message.timestamp;
};

export const PIN_TYPES = {
  PHOTO: {
    id: 'PHOTO',
    name: 'Photo',
    filter: (message) => {
      const metadata = getMetadata(message);
      return !!(
        metadata.imageUrl && 
        isValidLocation(metadata.location)
      );
    },
    icon: 'photo',
    priority: 1,
    color: '#3B82F6', // Blue
    enabled: true
  },
  
  // Future pin types for extensibility
  AUDIO: {
    id: 'AUDIO', 
    name: 'Audio',
    filter: (message) => {
      const metadata = getMetadata(message);
      return !!(
        metadata.audioUrl && 
        isValidLocation(metadata.location)
      );
    },
    icon: 'audio',
    priority: 2,
    color: '#10B981', // Green
    enabled: false // Disabled until implemented
  },
  
  DISCOVERY: {
    id: 'DISCOVERY',
    name: 'Discovery',
    filter: (message) => {
      const metadata = getMetadata(message);
      return !!(
        metadata.discovery && 
        isValidLocation(metadata.location)
      );
    },
    icon: 'star',
    priority: 3,
    color: '#F59E0B', // Yellow
    enabled: false // Disabled until implemented
  },
  
  MILESTONE: {
    id: 'MILESTONE',
    name: 'Milestone', 
    filter: (message) => {
      const metadata = getMetadata(message);
      return !!(
        metadata.milestone && 
        isValidLocation(metadata.location)
      );
    },
    icon: 'flag',
    priority: 4,
    color: '#EF4444', // Red
    enabled: false // Disabled until implemented
  }
};

/**
 * Get enabled pin types
 */
export const getEnabledPinTypes = () => {
  return Object.values(PIN_TYPES).filter(type => type.enabled);
};

/**
 * Filter messages to those that should appear as pins
 * @param {Array} messages - Array of chat messages
 * @param {Array} enabledTypes - Array of pin type IDs to include (default: all enabled)
 * @returns {Array} Array of message pin objects
 */
export const filterMessagesToPins = (messages = [], enabledTypes = null) => {
  if (!Array.isArray(messages)) return [];
  
  const typesToCheck = enabledTypes 
    ? enabledTypes.map(id => PIN_TYPES[id]).filter(Boolean)
    : getEnabledPinTypes();
  
  const pins = [];
  
  messages.forEach(message => {
    try {
      // Skip messages without required fields
      if (!message || !message.id) return;
      
      // Check each enabled pin type
      for (const pinType of typesToCheck) {
        if (pinType.filter(message)) {
          const metadata = getMetadata(message);
          const timestamp = getTimestamp(message);
          
          // Double-check location validity (filter should have caught this, but safety first)
          if (!isValidLocation(metadata.location)) continue;
          
          pins.push({
            messageId: message.id,
            type: pinType.id,
            pinType: pinType,
            message: message,
            location: metadata.location,
            content: message.content || '',
            timestamp: timestamp,
            metadata: metadata,
            // Additional data based on pin type
            imageUrl: metadata.imageUrl,
            audioUrl: metadata.audioUrl,
            discovery: metadata.discovery,
            milestone: metadata.milestone
          });
          
          // Only add message to one pin type (highest priority)
          break;
        }
      }
    } catch (error) {
      console.warn('Error processing message for pins:', error, message);
      // Continue processing other messages
    }
  });
  
  // Sort by priority then timestamp (newest first)
  return pins.sort((a, b) => {
    try {
      if (a.pinType.priority !== b.pinType.priority) {
        return a.pinType.priority - b.pinType.priority;
      }
      const timestampA = new Date(a.timestamp);
      const timestampB = new Date(b.timestamp);
      
      // Handle invalid dates
      if (isNaN(timestampA.getTime()) || isNaN(timestampB.getTime())) {
        return 0; // Keep original order if timestamps are invalid
      }
      
      return timestampB - timestampA;
    } catch (error) {
      console.warn('Error sorting pins:', error);
      return 0;
    }
  });
};

/**
 * Group pins by proximity for clustering using optimized algorithm
 * @param {Array} pins - Array of pin objects
 * @param {number} distanceThreshold - Distance in meters to consider pins "close"
 * @returns {Array} Array of pin clusters
 */
export const clusterPins = (pins = [], distanceThreshold = 50) => {
  if (!Array.isArray(pins) || pins.length === 0) return [];
  
  // For very large datasets, limit processing to prevent UI freezing
  const MAX_PINS_FOR_CLUSTERING = 200;
  const processablePins = pins.length > MAX_PINS_FOR_CLUSTERING 
    ? pins.slice(0, MAX_PINS_FOR_CLUSTERING)
    : pins;
  
  const clusters = [];
  const processed = new Set();
  
  processablePins.forEach((pin, index) => {
    try {
      if (processed.has(index)) return;
      
      // Validate pin has valid location
      if (!pin.location || !isValidLocation(pin.location)) {
        console.warn('Skipping pin with invalid location:', pin);
        processed.add(index);
        return;
      }
      
      const cluster = {
        id: `cluster-${pin.messageId}`,
        type: 'cluster',
        centerPin: pin,
        pins: [pin],
        location: pin.location,
        count: 1
      };
      
      // Find nearby pins (optimization: only check remaining pins)
      for (let otherIndex = index + 1; otherIndex < processablePins.length; otherIndex++) {
        if (processed.has(otherIndex)) continue;
        
        const otherPin = processablePins[otherIndex];
        
        // Validate other pin location
        if (!otherPin.location || !isValidLocation(otherPin.location)) {
          continue;
        }
        
        const distance = calculateDistance(
          pin.location.lat, pin.location.lng,
          otherPin.location.lat, otherPin.location.lng
        );
        
        // Skip invalid distances
        if (distance === Infinity) continue;
        
        if (distance <= distanceThreshold) {
          cluster.pins.push(otherPin);
          cluster.count++;
          processed.add(otherIndex);
        }
      }
      
      processed.add(index);
      clusters.push(cluster);
    } catch (error) {
      console.warn('Error clustering pin:', error, pin);
      processed.add(index);
    }
  });
  
  return clusters;
};

/**
 * Calculate distance between two coordinates in meters using Haversine formula
 * @param {number} lat1 - First latitude
 * @param {number} lng1 - First longitude
 * @param {number} lat2 - Second latitude
 * @param {number} lng2 - Second longitude
 * @returns {number} Distance in meters, or Infinity if coordinates are invalid
 */
function calculateDistance(lat1, lng1, lat2, lng2) {
  // Validate input coordinates
  if (
    typeof lat1 !== 'number' || typeof lng1 !== 'number' ||
    typeof lat2 !== 'number' || typeof lng2 !== 'number' ||
    isNaN(lat1) || isNaN(lng1) || isNaN(lat2) || isNaN(lng2)
  ) {
    return Infinity; // Invalid coordinates are infinitely far apart
  }
  
  // Same coordinates
  if (lat1 === lat2 && lng1 === lng2) {
    return 0;
  }
  
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI/180;
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lng2-lng1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  const distance = R * c;
  
  // Return Infinity for invalid results
  return isNaN(distance) ? Infinity : distance;
}

/**
 * Default pin configuration
 */
export const DEFAULT_PIN_CONFIG = {
  enabledTypes: ['PHOTO'], // Start with photos only
  clustering: true,
  clusterDistance: 50, // meters
  maxPins: 200, // Increased limit after optimization
  minZoomLevel: 8, // Show pins at broader zoom levels
  showInSidebar: true,
  // Performance settings
  processingChunkSize: 50, // Process pins in chunks to prevent UI freezing
  enableIconCaching: true, // Cache icons for better performance
  debounceMs: 100 // Debounce rapid updates
};
